import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { X, CheckCircle, XCircle } from 'lucide-react';
import { auth } from '../firebaseConfig';
import {
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { debounce } from 'lodash';

const primary = '#DBAE6E'; // Gold
const primaryDark = '#ffffff'; // White

const Login = ({ isOpen, setIsOpen }) => {
  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
  });
  const [state, setState] = useState({
    isSignUp: false,
    view: 'login',
    error: '',
    success: '',
    isLoading: false,
    animationType: null,
    activeButton: null, // Track which button is clicked
  });
  const emailInputRef = useRef(null);
  const authButtonControls = useAnimation();
  const googleButtonControls = useAnimation();

  const updateForm = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updateState = useCallback((updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetForm = useCallback(() => {
    setForm({ email: '', password: '', confirmPassword: '', name: '' });
  }, []);

  const showAnimation = useCallback((type, message) => {
    updateState({ animationType: type, [type === 'success' ? 'success' : 'error']: message });
    setTimeout(() => {
      updateState({ animationType: null, error: '', success: '' });
      if (type === 'success') {
        setIsOpen(false);
        resetForm();
        updateState({ view: 'login' });
      }
    }, 1500);
  }, [setIsOpen, resetForm, updateState]);

  const validateForm = useCallback(() => {
    const { email, password, confirmPassword, name } = form;
    if (!email.match(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/)) {
      return 'Please enter a valid email address';
    }
    if (state.isSignUp) {
      if (!name.trim()) return 'Name is required';
      if (password.length < 6) return 'Password must be at least 6 characters';
      if (password !== confirmPassword) return 'Passwords do not match';
    } else if (!password && !state.isForgotPassword) {
      return 'Password is required';
    }
    return null;
  }, [form, state.isSignUp]);

  const getFirebaseErrorMessage = (errorCode) => {
    const errorMessages = {
      'auth/email-already-in-use': 'User already exist. Please Login',
      'auth/invalid-email': 'Invalid E-mail',
      'auth/user-not-found': 'No account found. Please sign up.',
      'auth/wrong-password': 'Incorrect password.',
      'auth/too-many-requests': 'Too many attempts. Try later',
      'auth/invalid-credential': 'Invalid credentials.',
      'auth/operation-not-allowed': 'Try using another method',
      'auth/user-disabled': 'Account Disabled.',
      'auth/invalid-verification-code': 'Invalid verification code.',
      'auth/invalid-verification-id': 'Invalid verification ID.',
      'auth/network-request-failed': 'Network error.',
    };
    return errorMessages[errorCode] || 'Please try again.';
  };

  const handleGoogleLogin = useCallback(
    debounce(async () => {
      updateState({ isLoading: true, error: '', activeButton: 'google' });
      await googleButtonControls.start({ scale: 0.95 });
      try {
        await signInWithPopup(auth, new GoogleAuthProvider());
        showAnimation('success', 'Logged in successfully!');
      } catch (error) {
        showAnimation('failed', getFirebaseErrorMessage(error.code));
      } finally {
        updateState({ isLoading: false, activeButton: null });
        await googleButtonControls.start({ scale: 1 });
      }
    }, 300, { leading: true, trailing: false }),
    [googleButtonControls, showAnimation, updateState]
  );

  const handleEmailAuth = useCallback(
    debounce(async (e) => {
      e.preventDefault();
      const validationError = validateForm();
      if (validationError) {
        showAnimation('failed', validationError);
        return;
      }

      updateState({ isLoading: true, error: '', activeButton: 'auth' });
      await authButtonControls.start({ scale: 0.95 });

      try {
        const { email, password, name } = form;
        if (state.isSignUp) {
          const userCredential = await createUserWithEmailAndPassword(auth, email, password);
          await updateProfile(userCredential.user, { displayName: name });
          showAnimation('success', 'Account created successfully!');
        } else {
          await signInWithEmailAndPassword(auth, email, password);
          showAnimation('success', 'Logged in successfully!');
        }
      } catch (error) {
        showAnimation('failed', getFirebaseErrorMessage(error.code));
      } finally {
        updateState({ isLoading: false, activeButton: null });
        await authButtonControls.start({ scale: 1 });
      }
    }, 300, { leading: true, trailing: false }),
    [authButtonControls, form, state.isSignUp, showAnimation, updateState, validateForm]
  );

  const handleForgotPassword = useCallback(
    debounce(async (e) => {
      e.preventDefault();
      const { email } = form;
      if (!email.match(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/)) {
        showAnimation('failed', 'Please enter a valid email address');
        return;
      }

      updateState({ isLoading: true, error: '', activeButton: 'auth' });
      await authButtonControls.start({ scale: 0.95 });

      try {
        await sendPasswordResetEmail(auth, email);
        showAnimation('success', 'Password reset link sent to your email!');
      } catch (error) {
        showAnimation('failed', getFirebaseErrorMessage(error.code));
      } finally {
        updateState({ isLoading: false, activeButton: null });
        await authButtonControls.start({ scale: 1 });
      }
    }, 300, { leading: true, trailing: false }),
    [authButtonControls, form, showAnimation, updateState]
  );

  useEffect(() => {
    if (state.view === 'forgotPassword' && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [state.view]);

  if (!isOpen) return null;

  const isForgotPassword = state.view === 'forgotPassword';
  const inputStyle = 'w-full p-3 bg-gray-50 text-black rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#DBAE6E] transition-all placeholder-gray-400';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4"
    >
      <motion.div
        key={state.view}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="bg-white text-black rounded-2xl p-8 w-full max-w-md shadow-2xl relative"
      >
        <button
          type="button"
          aria-label="Close modal"
          className="absolute top-4 right-4 text-[#DBAE6E] hover:text-gray-600 transition-colors"
          onClick={() => {
            setIsOpen(false);
            resetForm();
            updateState({ view: 'login', error: '', success: '' });
          }}
        >
          <X size={24} />
        </button>
        <div className="flex flex-col space-y-6">
          <h2 className="text-3xl font-bold text-[#DBAE6E] mb-6 text-center">
            {isForgotPassword ? 'Reset Password' : state.isSignUp ? 'Join with Laynered' : 'Welcome Back'}
          </h2>
          <form onSubmit={isForgotPassword ? handleForgotPassword : handleEmailAuth} noValidate className="space-y-4">
            {state.isSignUp && !isForgotPassword && (
              <input
                type="text"
                placeholder="Full Name"
                value={form.name}
                onChange={(e) => updateForm('name', e.target.value)}
                className={inputStyle}
                aria-label="Full Name"
                required={state.isSignUp}
              />
            )}
            <input
              type="email"
              placeholder={isForgotPassword ? 'Enter your email' : 'Email'}
              value={form.email}
              onChange={(e) => updateForm('email', e.target.value)}
              className={inputStyle}
              aria-label={isForgotPassword ? 'Email for password reset' : 'Email'}
              ref={isForgotPassword ? emailInputRef : null}
              required
            />
            {!isForgotPassword && (
              <>
                <input
                  type="password"
                  placeholder="Password"
                  value={form.password}
                  onChange={(e) => updateForm('password', e.target.value)}
                  className={inputStyle}
                  aria-label="Password"
                  required
                />
                {state.isSignUp && (
                  <input
                    type="password"
                    placeholder="Confirm Password"
                    value={form.confirmPassword}
                    onChange={(e) => updateForm('confirmPassword', e.target.value)}
                    className={inputStyle}
                    aria-label="Confirm Password"
                    required
                  />
                )}
              </>
            )}
            {state.error && state.animationType === 'failed' && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-500 text-sm text-center"
                role="alert"
              >
                {state.error}
              </motion.p>
            )}
            <motion.button
              type="submit"
              animate={state.activeButton === 'auth' ? authButtonControls : {}}
              disabled={state.isLoading}
              className="w-full px-4 py-3 bg-[#DBAE6E] text-black rounded-lg hover:bg-[#C89D5D] transition-colors duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {state.isLoading && state.activeButton === 'auth' ? (
                <span className="w-6 h-6 border-4 border-t-transparent border-black rounded-full animate-spin" />
              ) : isForgotPassword ? (
                'Send Reset Link'
              ) : state.isSignUp ? (
                'Create Account'
              ) : (
                'Login'
              )}
            </motion.button>
          </form>
          {!isForgotPassword && (
            <motion.button
              animate={state.activeButton === 'google' ? googleButtonControls : {}}
              onClick={handleGoogleLogin}
              disabled={state.isLoading}
              className="w-full px-4 py-3 bg-white border border-gray-200 text-black rounded-lg hover:bg-gray-100 transition-colors duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {state.isLoading && state.activeButton === 'google' ? (
                <span className="w-6 h-6 border-4 border-t-transparent border-black rounded-full animate-spin" />
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C4.01 20.69 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 4.01 3.31 2.18 6.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Continue with Google
                </>
              )}
            </motion.button>
          )}
          <div className="flex justify-between">
            {!state.isSignUp && !isForgotPassword && (
              <button
                type="button"
                className="text-[#DBAE6E] text-sm hover:underline focus:outline-none transition-colors"
                onClick={() => {
                  resetForm();
                  updateState({ view: 'forgotPassword' });
                }}
              >
                Forgot Password?
              </button>
            )}
            <button
              type="button"
              className="text-[#DBAE6E] text-sm hover:underline focus:outline-none transition-colors"
              onClick={() => {
                resetForm();
                updateState({ isSignUp: !state.isSignUp, view: 'login' });
              }}
            >
              {isForgotPassword
                ? 'Back to Login'
                : state.isSignUp
                ? 'Already have an account? Login'
                : "Don't have an account? Sign Up"}
            </button>
          </div>
        </div>
      </motion.div>
      {state.animationType && (
        <motion.div
          initial={{ opacity: 0, y: -100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ type: 'spring', stiffness: 120, damping: 20 }}
          className={`fixed inset-0 flex items-center justify-center z-[70] ${
            state.animationType === 'success' ? 'bg-green-500' : 'bg-red-500'
          } bg-opacity-90`}
        >
          <motion.div
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="flex flex-col items-center"
          >
            {state.animationType === 'success' ? (
              <CheckCircle size={80} className="text-white" />
            ) : (
              <XCircle size={80} className="text-white" />
            )}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="text-white text-lg font-semibold mt-4 text-center px-4 max-w-sm"
            >
              {state[state.animationType === 'success' ? 'success' : 'error']}
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default Login;